import { isValidStyleProperty } from '../../../core/utilities/swan-style.utils.js';
import { className } from '../../../core/utilities/classnames.utils.js';
import { processStyleProps } from '../../../core/utilities/swan-style.js';
import { iterToSet } from '../../../core/utilities/transform.utils.js';

/**
 * FOR DP, it could be undefined by the configuration,
 * in that case, it would result in type undefined, as defaults in createComponent
 */ function getDerivedProps(derivedProps, props) {
    if (typeof derivedProps === 'function') {
        return derivedProps(props);
    }
    return derivedProps;
}
function getProcessedProps(processProps, props, derivedProps) {
    if (typeof processProps === 'function') {
        return processProps(props, derivedProps);
    }
    return props;
}
function getDefaultClassNameIterable(classNames, props, derivedProps) {
    if (!classNames) return null;
    if (typeof classNames === 'function') {
        return classNames(props, derivedProps);
    }
    // If the defaultClassname is not a function, it could share the initial classnames with multiple instances
    // Hence, creating a new set.
    // Additionally, inside renderElement, it would be sent to process Styles which would re-use this.
    return new Set(classNames);
}
// Filter Props
const commonPropKeys = new Set([
    'as',
    'component',
    'render',
    'swanStyle',
    'className',
    'children',
    'htmlSize',
    '__dangerouslySuppressWarning',
    'experiment'
]);
function isValidGlobalProps(key) {
    return commonPropKeys.has(key);
}
function shouldFilterPropKeyFactory(filterPropKeys) {
    if (!filterPropKeys) return ()=>false;
    const filterPropKeySet = iterToSet(filterPropKeys);
    return function shouldFilterPropKey(key) {
        return filterPropKeySet.has(key);
    };
}
function filterProps(props, filterPropKeys, classNameIterable) {
    const shouldFilterPropKey = shouldFilterPropKeyFactory(filterPropKeys);
    const filteredPropsEntries = Object.entries(props).filter(([key])=>!(isValidStyleProperty(key) || isValidGlobalProps(key) || shouldFilterPropKey(key)));
    const classes = className(props.className, classNameIterable ? Array.from(classNameIterable) : null);
    if (classes) filteredPropsEntries.push([
        'className',
        classes
    ]);
    const htmlSize = props['htmlSize'];
    if (htmlSize !== undefined) filteredPropsEntries.push([
        'size',
        htmlSize
    ]);
    return Object.fromEntries(filteredPropsEntries);
}
function wrap(wrapper, props, derivedProps) {
    return (element)=>{
        return wrapper(props, derivedProps, element);
    };
}
function getProps(userProps, propConfig, filterPropKeys) {
    const { derivedProps, processProps, classNames, wrapRoot, wrapChildren } = propConfig || {};
    const derivedExtraProps = getDerivedProps(derivedProps, userProps);
    const processedProps = getProcessedProps(processProps, userProps, derivedExtraProps);
    const defaultClassNameIterable = getDefaultClassNameIterable(classNames, processedProps, derivedExtraProps);
    const classNameIterable = processStyleProps(processedProps, defaultClassNameIterable);
    const filteredProps = filterProps(processedProps, filterPropKeys, classNameIterable);
    return {
        processedProps,
        props: filteredProps,
        wrapRoot: wrapRoot ? wrap(wrapRoot, processedProps, derivedExtraProps) : null,
        children: wrapChildren ? wrapChildren(processedProps, derivedExtraProps, processedProps.children) : processedProps.children
    };
}

export { filterProps, getDefaultClassNameIterable, getDerivedProps, getProcessedProps, getProps, wrap };
